import { useState } from 'react';
import { X, Play } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedMedia, setSelectedMedia] = useState(null);

  const galleryItems = [
    {
      url: 'https://drive.google.com/file/d/1kg0j852VfFleQ7Rt21LCqGFUNWiQJznr/preview',
      thumbnail: 'https://lh3.googleusercontent.com/u/0/d/1XAw22QQSxLyi4ZKVako6Y0XGBjvKtJmo',
      category: 'facade',
      alt: 'Fachada, Exterior e Interior',
      type: 'video'
    },
    {
      url: 'https://drive.google.com/file/d/1tNusMGad2iGVjCJov26oi97ErywjYKJG/preview',
      thumbnail: 'https://lh3.googleusercontent.com/u/0/d/1tNusMGad2iGVjCJov26oi97ErywjYKJG',
      category: 'diner',
      alt: 'Jantar Especial',
      type: 'video'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/1kCI8xQRVsF5JWh3cqhz-1oT1eMJBQlNY',
      category: 'standard',
      alt: 'Apartamento Standard',
      type: 'image'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/1wyNuFuEWs3n8mPwVL0cutinlrAh6uBVs',
      category: 'junior',
      alt: 'Suíte Junior',
      type: 'image'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/1TY9ZBQM_ALZfQ5KZUja2lH1JUc3Ery4j',
      category: 'executive',
      alt: 'Suíte Executiva',
      type: 'image'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/19DGOGlOMpGqBviLnfYvqfQ6w4Zk7O6IS',
      category: 'master',
      alt: 'Suíte Master',
      type: 'image'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/1hrjaU7T4GjIzY_wtYj5S1NqcreRFOxoo',
      category: 'common',
      alt: 'Hall de Entrada',
      type: 'image'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/17ZMrSUPvnKYy_xZshIqMzXEWuwWKJSSG',
      category: 'details',
      alt: 'Detalhes de Decoração',
      type: 'image'
    },
    {
      url: 'https://drive.google.com/file/d/1llIzxlihZx9MwiHQOI0o82fBlTshjPKH/preview',
      thumbnail: 'https://lh3.googleusercontent.com/u/0/d/1VFhrAdS53EuWhp1hZiPRc4uWMA3gLfK-',
      category: 'media',
      alt: 'Mondego na Mídia',
      type: 'video'
    },
    {
      url: 'https://lh3.googleusercontent.com/u/0/d/1QTjAakfliFI_JTwBmuX5d8-ZRVhe2H7s',
      category: 'breakfast',
      alt: 'Café da Manhã',
      type: 'image'
    }
  ];

  const categoryKeys = ['facade', 'standard', 'junior', 'executive', 'master', 'common', 'details', 'media', 'breakfast'];
  const categories = [...new Set(galleryItems.map((item) => item.category))].sort((a, b) => {
    return categoryKeys.indexOf(a) - categoryKeys.indexOf(b);
  });

  const getCategoryName = (categoryKey) => {
    return t(`gallery.categories.${categoryKey}`);
  };

  return (
    <div className="min-h-screen bg-[#f6f6f8] pt-28 pb-20">
      {/* Hero */}
      <div
        className="relative h-[400px] mb-20 parallax"
        style={{
          backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuCL5s6IWWRdYQWRbMoCgxMuxSqNHZuuX2-rHIf3npYuQHoFaoXro2mgHMKiktBtspB14LLCx_V9_2d_p9_dNgd7VS3h5kVim0fVfuVvJiJ1xTMyL0IUUdxlhEK136NHgXj-UCBPGhkdiB7OjSEQSMValFEDPJJtXsaqGXWcGwXj8RRuBBBJen42P3jWBfIKzOkqLZNs6twQ4iMzX_rogdzTNNXjXrG9PjXNuBJdyxNp_4oOiWFdRrKH-pm1IICoM7XZ0nMmzCVfVys")'
        }}
        data-testid="gallery-hero"
      >
        <div className="absolute inset-0 bg-[#101B2D]/60"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-white animate-fade-in">{t('gallery.title')}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Description */}
        <div className="text-center mb-12 animate-slide-up" data-testid="gallery-intro">
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('gallery.description')}
          </p>
        </div>

        {/* Gallery by Category */}
        {categories.map((category, catIdx) => (
          <div key={catIdx} className="mb-16 animate-slide-up" style={{ animationDelay: `${catIdx * 0.1}s` }} data-testid={`gallery-category-${catIdx}`}>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">{getCategoryName(category)}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryItems
                .filter((item) => item.category === category)
                .map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    onClick={() => setSelectedMedia(item)}
                    className="relative h-80 rounded-xl overflow-hidden cursor-pointer hover-scale image-hover-zoom group"
                    data-testid={`gallery-item-${catIdx}-${itemIdx}`}
                  >
                    {item.type === 'video' ? (
                      <>
                        <img 
                          src={item.thumbnail} 
                          alt={item.alt} 
                          className="w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 bg-[#C5A059] rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                            <Play className="w-10 h-10 text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <img src={item.url} alt={item.alt} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white font-semibold">{item.alt}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Media Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
          data-testid="gallery-modal"
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
            onClick={() => setSelectedMedia(null)}
            data-testid="close-gallery-modal"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'video' ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={selectedMedia.url}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full rounded-xl shadow-2xl"
                  title={selectedMedia.alt}
                />
              </div>
            ) : (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.alt}
                className="w-full h-auto rounded-xl shadow-2xl"
              />
            )}
            <div className="text-center mt-6">
              <p className="text-white text-lg font-semibold">{selectedMedia.alt}</p>
              <p className="text-gray-400 text-sm">{getCategoryName(selectedMedia.category)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
